/**
 * What the operator's console sets for everyone who has not chosen for
 * themselves (QA CO-BUG-15): the starting theme and voice input mode
 * (`defaults.*`), and the app's name (`texts.app_name`) on the tab and the
 * wordmark. A choice made in this app (saved on this device) always
 * wins; nothing here is saved, so a later console change still applies.
 *
 * Signed in, the theme is the account's (store/look.ts); an account made
 * on this device starts with the one the app showed at sign-up, the
 * console's default included. Signed out again, the defaults
 * are back (QA 2026-10-28: a device that never chose stayed Smoked after a
 * Smoked account signed out, whatever `defaults.theme` said).
 */
import type { ClientConfig, LocalizedText } from '@neosocial/protocol';
import { useI18n } from '../i18n';
import { hasSavedSession } from '../lib/accountLook';
import { storage } from '../lib/storage';
import { useVoice } from '../voice/store';
import { onConfigChange, useConfig } from './config';
import { applyTheme, useUi } from './ui';


/** Applies the console's defaults where this device has no choice of its own. */
export function applyConsoleDefaults(c: ClientConfig): void {
  const s = c.settings;
  // signed in, the account's look is shown (applyAccountLook), not the defaults
  const account = hasSavedSession();
  const theme = s['defaults.theme'];
  if (theme && !account && !storage.get('theme') && useUi.getState().theme !== theme) {
    applyTheme(theme);
    useUi.getState().set({ theme });
  }

  const mode = s['defaults.voice_mode'];
  const saved = storage.getJson<{ inputMode?: string }>('voice-prefs', {});
  if (mode && !saved.inputMode) {
    const inputMode = mode === 'push_to_talk' ? 'ptt' : 'va';
    if (useVoice.getState().prefs.inputMode !== inputMode)
      useVoice.setState((v) => ({ prefs: { ...v.prefs, inputMode } }));
  }
}

let wired = false;
/** Once at start: every accepted config applies the defaults again. */
export function wireConsoleDefaults(): void {
  if (wired) return;
  wired = true;
  onConfigChange((next) => applyConsoleDefaults(next));
}

function pick(t: LocalizedText | undefined): string {
  return t?.en ?? '';
}

/** The app's name as the console sets it (`texts.app_name`). */
export function useAppName(): string {
  const brand = useI18n((s) => s.tr.t('ns.brand'));
  const name = useConfig((s) => s.config?.settings['texts.app_name']);
  return pick(name).trim() || brand;
}

/** The wordmark: the name in capitals. */
export function useWordmark(): string {
  const name = useConfig((s) => s.config?.settings['texts.app_name']);
  return (name?.en || 'Neosocial').trim().toUpperCase();
}
